import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    OnDestroy,
    OnInit,
    computed,
    effect,
    inject,
    input,
    output,
    signal,
    viewChild,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Subscription } from 'rxjs';

import { MediaVideo } from '../../../_shared/models/media/media-video.model';
import { ICustomWindow, WindowRefService } from '../../../_shared/services/window-ref.service';
import { isEmbeddedVideoSource } from '../../../_shared/types';
import { MediaItemObjectFit } from '../../../_shared/types/media/media-item-object-fit.type';
import { PlayButtonComponent } from '../../../button/play-button/play-button.component';
import { MediaItem } from '../media-item.directive';
import { VideoItemService } from './video-item.service';
import { IPlayer, PlayerDefault, PlayerTwentyThree } from './video-player.model';

@Component({
    selector: 'sirius-video-item',
    templateUrl: './video-item.component.html',
    styleUrls: ['./video-item.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [PlayButtonComponent],
    host: {
        '[class.is-fluid]': 'fluid()',
        '(click)': 'onClick()',
        '(mouseenter)': 'onMouseEnter()',
        '(mouseleave)': 'onMouseLeave()',
    },
})
export class VideoItemComponent extends MediaItem implements OnInit, AfterViewInit, OnDestroy {
    readonly video = input.required<MediaVideo>();
    readonly objectFit = input<MediaItemObjectFit>('cover');
    readonly fluid = input(false);
    readonly canPlay = input(true);

    readonly loaded = output<void>();

    readonly videoRef = viewChild<ElementRef<HTMLMediaElement>>('videoelement');
    readonly iframeRef = viewChild<ElementRef<HTMLIFrameElement>>('iframe');

    readonly isEmbedded = computed(() => isEmbeddedVideoSource(this.video().videoSource));
    readonly videoUrl = computed(() => this.video().url);
    readonly safeVideoUrl = computed(() => this._sanitizer.bypassSecurityTrustResourceUrl(this.video().url));
    readonly thumbnailUrl = computed(() => this.video().thumbnail?.url ?? null);
    readonly title = computed(() => this.video().title);

    readonly isInit = signal(false);
    readonly isPlaying = signal(false);
    readonly isHovered = signal(false);

    player?: IPlayer;

    private _index = 0;
    private _subscriptions = new Subscription();
    private _sanitizer = inject(DomSanitizer);
    private _videoItemService = inject(VideoItemService);
    private _windowRef = inject(WindowRefService);

    constructor() {
        super();

        // Pause this video when its slot is no longer the active one.
        effect(() => {
            if (!this.canPlay() && this.isPlaying() && this.player) {
                this.player.pause();
            }
        });
    }

    private get Window(): ICustomWindow | undefined {
        return this._windowRef.NativeWindow;
    }

    onClick(): void {
        if (!this.isInit()) {
            this.init();
            return;
        }

        if (this.isPlaying()) {
            this.player?.pause();
        } else {
            this.player?.play();
        }
    }

    onMouseEnter(): void {
        this.isHovered.set(true);
    }

    onMouseLeave(): void {
        this.isHovered.set(false);
    }

    ngOnInit(): void {
        this._index = this._videoItemService.addVideo(this);
    }

    ngAfterViewInit(): void {
        if (!this.Window) {
            return;
        }

        const iframeEl = this.iframeRef()?.nativeElement;
        const videoEl = this.videoRef()?.nativeElement;
        const source = this.video().videoSource;

        if (iframeEl && source === 'twentyThreeEmbedded') {
            this.player = new PlayerTwentyThree(iframeEl);
        } else if (videoEl) {
            this.player = new PlayerDefault(videoEl);
        }

        if (this.player) {
            this._subscriptions.add(
                this.player.playing$.subscribe((x) => this.isPlaying.set(x)),
            );
        }
    }

    ngOnDestroy(): void {
        this._videoItemService.removeVideo(this._index);
        this._subscriptions.unsubscribe();
    }

    init(): void {
        if (!this.canPlay()) {
            return;
        }

        this.isInit.set(true);
        this._videoItemService.pauseAll();
        this.player?.play();
    }

    onLoaded(): void {
        this.loaded.emit();
    }
}
